'use client'

import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdLocalOffer, MdArrowBack, MdClose, MdSearch } from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    getPromotion,
    createPromotion,
    updatePromotion,
} from '@/lib/api/commerce/promos'
import {
    getCategoriesOnlyByCompany,
    getSubcategoriesByParent,
    ProductCategoryItem,
} from '@/lib/api/product-category/product-category-api'
import { getCatalogsByCompany, catalogBasePrice } from '@/lib/api/catalog/catalog-api'
import { getStoreConfigByCompany, StoreConfig } from '@/lib/api/commerce/store-config'

type PromoType = 'CATEGORY' | 'SUBCATEGORY' | 'MINIMUM_PURCHASE' | 'SPECIFIC_PRODUCTS' | 'ALL_STORE'
type DiscountType = 'PERCENTAGE' | 'FIXED_AMOUNT'

interface ProductItem {
    id: number
    name: string
    photo?: string | null
    price: number
}

export default function PromosFormPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const isEditing = !!id

    // Form fields
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [type, setType] = useState<PromoType>('CATEGORY')
    const [discountType, setDiscountType] = useState<DiscountType>('PERCENTAGE')
    const [discountValue, setDiscountValue] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [active, setActive] = useState(true)

    // Type-dependent fields
    const [categoryId, setCategoryId] = useState<number | null>(null)
    const [subcategoryId, setSubcategoryId] = useState<number | null>(null)
    const [minimumPurchase, setMinimumPurchase] = useState('')
    const [selectedProductIds, setSelectedProductIds] = useState<number[]>([])

    // Data sources
    const [categories, setCategories] = useState<ProductCategoryItem[]>([])
    const [subcategories, setSubcategories] = useState<ProductCategoryItem[]>([])
    const [products, setProducts] = useState<ProductItem[]>([])
    const [productSearch, setProductSearch] = useState('')
    const [availableStores, setAvailableStores] = useState<StoreConfig[]>([])
    const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([])

    // UI state
    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)
    const [loadingSubcategories, setLoadingSubcategories] = useState(false)

    useEffect(() => {
        if (user?.companyId) {
            loadCategories()
            loadProducts()
            loadStores()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (isEditing && id) {
            loadPromotion()
        }
    }, [id])

    useEffect(() => {
        if (categoryId && (type === 'SUBCATEGORY')) {
            loadSubcategories(categoryId)
        } else {
            setSubcategories([])
            setSubcategoryId(null)
        }
    }, [categoryId, type])

    const loadCategories = async () => {
        if (!user?.companyId) return
        try {
            const response = await getCategoriesOnlyByCompany(user.companyId)
            if (response.success) {
                setCategories(response.result)
            }
        } catch {
            // silenciar erro
        }
    }

    const loadSubcategories = async (parentId: number) => {
        setLoadingSubcategories(true)
        try {
            const response = await getSubcategoriesByParent(parentId)
            if (response.success) {
                setSubcategories(response.result)
            }
        } catch {
            // silenciar erro
        } finally {
            setLoadingSubcategories(false)
        }
    }

    const loadProducts = async () => {
        if (!user?.companyId) return
        try {
            // Mercadoria é item de catálogo (`category = PRODUCT`) desde a unificação.
            const response = await getCatalogsByCompany(user.companyId, {
                category: 'PRODUCT',
                activeOnly: true,
                isListing: true,
                limit: 200,
            })
            if (response.success) {
                const items = Array.isArray(response.result) ? response.result : []
                setProducts(
                    items.map((p: any) => ({
                        id: p.id,
                        name: p.name,
                        photo: p.media?.[0]?.url ?? null,
                        price: catalogBasePrice(p, user.companyId),
                    }))
                )
            }
        } catch {
            // silenciar erro
        }
    }

    const loadStores = async () => {
        if (!user?.companyId) return
        try {
            const response = await getStoreConfigByCompany(user.companyId)
            if (response?.success) {
                const stores = Array.isArray(response.result) ? response.result : [response.result]
                setAvailableStores(stores.filter(Boolean))
            }
        } catch {
            // silenciar erro
        }
    }

    const loadPromotion = async () => {
        if (!id) return
        setLoadingData(true)
        try {
            const response = await getPromotion(parseInt(id, 10))
            if (response.success) {
                const promo = response.result
                setName(promo.name)
                setDescription(promo.description ?? '')
                setType(promo.type)
                setDiscountType(promo.discountType)
                setDiscountValue(
                    promo.discountType === 'FIXED_AMOUNT'
                        ? (promo.discountValue / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : String(promo.discountValue)
                )
                setStartDate(promo.startDate ? promo.startDate.substring(0, 10) : '')
                setEndDate(promo.endDate ? promo.endDate.substring(0, 10) : '')
                setActive(promo.active)
                setCategoryId(promo.categoryId ?? null)
                setSubcategoryId(promo.subcategoryId ?? null)
                setMinimumPurchase(
                    promo.minimumPurchase
                        ? (promo.minimumPurchase / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : ''
                )
                if (promo.products && promo.products.length > 0) {
                    setSelectedProductIds(
                        promo.products.map((p) => p.catalogId)
                    )
                }
                if (promo.stores && promo.stores.length > 0) {
                    setSelectedStoreIds(promo.stores.map((s: any) => s.storeConfigId))
                }
            } else {
                toast.error(response.error || 'Erro ao carregar promoção')
                navigate('/commerce/promos')
            }
        } catch {
            toast.error('Erro ao carregar promoção')
            navigate('/commerce/promos')
        } finally {
            setLoadingData(false)
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!name.trim()) {
            toast.error('Nome é obrigatório')
            return
        }

        if (!startDate || !endDate) {
            toast.error('Datas de início e término são obrigatórias')
            return
        }

        if (!discountValue || parseFloat(discountValue) <= 0) {
            toast.error('Valor do desconto deve ser maior que zero')
            return
        }

        if (discountType === 'PERCENTAGE' && parseFloat(discountValue) > 100) {
            toast.error('Percentual de desconto não pode ultrapassar 100%')
            return
        }

        if ((type === 'CATEGORY' || type === 'SUBCATEGORY') && !categoryId) {
            toast.error('Selecione uma categoria')
            return
        }

        if (type === 'SUBCATEGORY' && !subcategoryId) {
            toast.error('Selecione uma subcategoria')
            return
        }

        if (type === 'MINIMUM_PURCHASE' && (!minimumPurchase || parseFloat(minimumPurchase) <= 0)) {
            toast.error('Valor mínimo de compra deve ser maior que zero')
            return
        }

        if (type === 'SPECIFIC_PRODUCTS' && selectedProductIds.length === 0) {
            toast.error('Selecione ao menos um produto')
            return
        }

        if (!user?.companyId) {
            toast.error('Empresa não identificada')
            return
        }

        const payload: Record<string, any> = {
            name: name.trim(),
            description: description.trim() || undefined,
            type,
            discountType,
            discountValue:
                discountType === 'FIXED_AMOUNT'
                    ? Math.round(parseFloat(discountValue) * 100)
                    : parseFloat(discountValue),
            startDate: new Date(startDate + 'T00:00:00').toISOString(),
            endDate: new Date(endDate + 'T23:59:59').toISOString(),
            active,
            companyId: user.companyId,
            storeConfigIds: selectedStoreIds.length > 0 ? selectedStoreIds : undefined,
        }

        if (type === 'CATEGORY') {
            payload.categoryId = categoryId
        } else if (type === 'SUBCATEGORY') {
            payload.categoryId = categoryId
            payload.subcategoryId = subcategoryId
        } else if (type === 'MINIMUM_PURCHASE') {
            payload.minimumPurchase = Math.round(parseFloat(minimumPurchase) * 100)
        } else if (type === 'SPECIFIC_PRODUCTS') {
            payload.productIds = selectedProductIds
        }

        setLoading(true)
        try {
            const response = isEditing
                ? await updatePromotion(parseInt(id!, 10), payload)
                : await createPromotion(payload)

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Promoção atualizada com sucesso'
                        : 'Promoção criada com sucesso'
                )
                navigate('/commerce/promos')
            } else {
                toast.error(response.error || 'Erro ao salvar promoção')
            }
        } catch {
            toast.error('Erro ao salvar promoção')
        } finally {
            setLoading(false)
        }
    }

    const toggleProduct = (productId: number) => {
        setSelectedProductIds((prev) =>
            prev.includes(productId)
                ? prev.filter((pid) => pid !== productId)
                : [...prev, productId]
        )
    }

    const filteredProducts = products.filter((p) =>
        p.name.toLowerCase().includes(productSearch.toLowerCase())
    )

    const selectedProducts = products.filter((p) =>
        selectedProductIds.includes(p.id)
    )

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando promoção...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center gap-3">
                <button
                    onClick={() => navigate('/commerce/promos')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdLocalOffer className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Promoção' : 'Nova Promoção'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados da promoção'
                                : 'Cadastre uma nova promoção'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit}>
                {/* Informações Básicas */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4">
                        Informações Básicas
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Nome *</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Ex: Desconto de Verão"
                                className="input input-bordered w-full"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                autoFocus
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={type}
                                onChange={(e) => {
                                    setType(e.target.value as PromoType)
                                    setCategoryId(null)
                                    setSubcategoryId(null)
                                    setMinimumPurchase('')
                                    setSelectedProductIds([])
                                }}
                            >
                                <option value="ALL_STORE">Toda a Loja</option>
                                <option value="CATEGORY">Categoria</option>
                                <option value="SUBCATEGORY">Subcategoria</option>
                                <option value="MINIMUM_PURCHASE">Compra mínima</option>
                                <option value="SPECIFIC_PRODUCTS">Produtos específicos</option>
                            </select>
                        </div>

                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">Descrição</span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Descrição da promoção (opcional)"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                {/* Desconto */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mt-4">
                    <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4">
                        Desconto
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Tipo de Desconto *</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={discountType}
                                onChange={(e) => {
                                    setDiscountType(e.target.value as DiscountType)
                                    setDiscountValue('')
                                }}
                            >
                                <option value="PERCENTAGE">Percentual (%)</option>
                                <option value="FIXED_AMOUNT">Valor fixo (R$)</option>
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Valor do Desconto *
                                </span>
                            </label>
                            <label className="input input-bordered flex items-center gap-2 w-full">
                                {discountType === 'FIXED_AMOUNT' && (
                                    <span className="text-base-content/50 text-sm">R$</span>
                                )}
                                <input
                                    type="number"
                                    step={discountType === 'FIXED_AMOUNT' ? '0.01' : '1'}
                                    min="0"
                                    max={discountType === 'PERCENTAGE' ? '100' : undefined}
                                    placeholder={
                                        discountType === 'PERCENTAGE' ? 'Ex: 15' : 'Ex: 25,00'
                                    }
                                    className="grow bg-transparent outline-none"
                                    value={discountValue}
                                    onChange={(e) => setDiscountValue(e.target.value)}
                                    required
                                />
                                {discountType === 'PERCENTAGE' && (
                                    <span className="text-base-content/50 text-sm">%</span>
                                )}
                            </label>
                            <label className="label">
                                <span className="label-text-alt text-base-content/50">
                                    {discountType === 'PERCENTAGE'
                                        ? 'Percentual de 0 a 100'
                                        : 'Valor em reais (ex: 25,00)'}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Período e Status */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mt-4">
                    <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4">
                        Período e Status
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Data de Início *</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Data de Término *</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-control w-full md:col-span-2">
                            <label className="label cursor-pointer justify-start gap-3">
                                <input
                                    type="checkbox"
                                    className="toggle toggle-secondary"
                                    checked={active}
                                    onChange={(e) => setActive(e.target.checked)}
                                />
                                <span className="label-text font-medium">
                                    Promoção ativa
                                </span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Lojas */}
                {availableStores.length > 0 && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 p-6 mt-4">
                        <h3 className="text-base font-semibold mb-1 pb-3 border-b border-base-300/50">
                            Lojas
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            Selecione as lojas onde esta promoção será ativada. Deixe vazio para aplicar em todas.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {availableStores.map((store) => (
                                <label key={store.id} className="flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors hover:bg-base-200/50">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary checkbox-sm"
                                        checked={selectedStoreIds.includes(store.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedStoreIds([...selectedStoreIds, store.id])
                                            } else {
                                                setSelectedStoreIds(selectedStoreIds.filter((id) => id !== store.id))
                                            }
                                        }}
                                    />
                                    <div className="flex items-center gap-2">
                                        {store.logo && (
                                            <img src={store.logo} alt={store.name} className="w-8 h-8 rounded object-cover" />
                                        )}
                                        <div>
                                            <p className="text-sm font-medium">{store.name}</p>
                                            <p className="text-xs text-base-content/50">{store.subdomain}</p>
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Condições - ALL_STORE */}
                {type === 'ALL_STORE' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mt-4">
                        <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4">
                            Toda a Loja
                        </h2>
                        <p className="text-sm text-base-content/60">
                            Desconto aplicado em todos os produtos da loja.
                        </p>
                    </div>
                )}

                {/* Condições - CATEGORY */}
                {(type === 'CATEGORY' || type === 'SUBCATEGORY') && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mt-4">
                        <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4">
                            {type === 'SUBCATEGORY'
                                ? 'Categoria e Subcategoria'
                                : 'Categoria'}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Categoria *</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={categoryId ?? ''}
                                    onChange={(e) =>
                                        setCategoryId(
                                            e.target.value ? parseInt(e.target.value, 10) : null
                                        )
                                    }
                                    required
                                >
                                    <option value="">Selecione uma categoria</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>
                                            {cat.name}
                                        </option>
                                    ))}
                                </select>
                                {categories.length === 0 && (
                                    <label className="label">
                                        <span className="label-text-alt text-warning">
                                            Nenhuma categoria cadastrada.
                                        </span>
                                    </label>
                                )}
                            </div>

                            {type === 'SUBCATEGORY' && (
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">
                                            Subcategoria *
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={subcategoryId ?? ''}
                                        onChange={(e) =>
                                            setSubcategoryId(
                                                e.target.value
                                                    ? parseInt(e.target.value, 10)
                                                    : null
                                            )
                                        }
                                        required
                                        disabled={!categoryId || loadingSubcategories}
                                    >
                                        <option value="">
                                            {loadingSubcategories
                                                ? 'Carregando...'
                                                : 'Selecione uma subcategoria'}
                                        </option>
                                        {subcategories.map((sub) => (
                                            <option key={sub.id} value={sub.id}>
                                                {sub.name}
                                            </option>
                                        ))}
                                    </select>
                                    {categoryId &&
                                        !loadingSubcategories &&
                                        subcategories.length === 0 && (
                                            <label className="label">
                                                <span className="label-text-alt text-warning">
                                                    Nenhuma subcategoria encontrada para esta
                                                    categoria.
                                                </span>
                                            </label>
                                        )}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Condições - MINIMUM_PURCHASE */}
                {type === 'MINIMUM_PURCHASE' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mt-4">
                        <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4">
                            Valor Mínimo de Compra
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">
                                        Valor mínimo (R$) *
                                    </span>
                                </label>
                                <label className="input input-bordered flex items-center gap-2 w-full">
                                    <span className="text-base-content/50 text-sm">R$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        placeholder="Ex: 100,00"
                                        className="grow bg-transparent outline-none"
                                        value={minimumPurchase}
                                        onChange={(e) => setMinimumPurchase(e.target.value)}
                                        required
                                    />
                                </label>
                                <label className="label">
                                    <span className="label-text-alt text-base-content/50">
                                        A promoção será aplicada quando o valor da compra atingir
                                        este mínimo
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Condições - SPECIFIC_PRODUCTS */}
                {type === 'SPECIFIC_PRODUCTS' && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6 mt-4">
                        <h2 className="text-base font-semibold text-base-content border-b border-base-300/50 pb-2 mb-4">
                            Produtos
                        </h2>

                        {/* Selected products badges */}
                        {selectedProducts.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-4">
                                {selectedProducts.map((product) => (
                                    <span
                                        key={product.id}
                                        className="badge badge-secondary badge-lg gap-1"
                                    >
                                        {product.name}
                                        <button
                                            type="button"
                                            onClick={() => toggleProduct(product.id)}
                                            className="ml-1"
                                        >
                                            <MdClose className="text-sm" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Search */}
                        <div className="form-control w-full mb-3">
                            <label className="input input-bordered flex items-center gap-2 w-full">
                                <MdSearch className="text-base-content/50" />
                                <input
                                    type="text"
                                    placeholder="Buscar produtos..."
                                    className="grow bg-transparent outline-none"
                                    value={productSearch}
                                    onChange={(e) => setProductSearch(e.target.value)}
                                />
                            </label>
                        </div>

                        {/* Product list */}
                        <div className="border border-base-300 rounded-lg max-h-64 overflow-y-auto">
                            {filteredProducts.length === 0 ? (
                                <div className="p-4 text-center text-base-content/50 text-sm">
                                    {products.length === 0
                                        ? 'Nenhum produto cadastrado'
                                        : 'Nenhum produto encontrado'}
                                </div>
                            ) : (
                                filteredProducts.map((product) => (
                                    <label
                                        key={product.id}
                                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer border-b border-base-300/50 last:border-b-0 transition-colors ${
                                            selectedProductIds.includes(product.id)
                                                ? 'bg-secondary/5'
                                                : 'hover:bg-base-200/50'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary checkbox-sm"
                                            checked={selectedProductIds.includes(product.id)}
                                            onChange={() => toggleProduct(product.id)}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium truncate">
                                                {product.name}
                                            </p>
                                            <p className="text-xs text-base-content/50">
                                                R$ {(product.price / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </p>
                                        </div>
                                    </label>
                                ))
                            )}
                        </div>

                        {selectedProductIds.length > 0 && (
                            <p className="text-sm text-base-content/60 mt-2">
                                {selectedProductIds.length}{' '}
                                {selectedProductIds.length === 1
                                    ? 'produto selecionado'
                                    : 'produtos selecionados'}
                            </p>
                        )}
                    </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 mt-6">
                    <button
                        type="button"
                        onClick={() => navigate('/commerce/promos')}
                        className="btn btn-ghost"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Salvando...
                            </>
                        ) : isEditing ? (
                            'Atualizar'
                        ) : (
                            'Criar Promoção'
                        )}
                    </button>
                </div>
            </form>
        </div>
    )
}
